import { useState } from "react";
import { ChevronDown } from "lucide-react";

const FAQS = [
  {
    q: "Do I need to be online during inspections?",
    a: "Inspections are saved as soon as your phone has connection. Voice memos and photos upload when you're back in range. You can start a journal entry offline and we'll sync it the next time you open the app.",
  },
  {
    q: "Is my data private?",
    a: "Yes. Your hives, photos and journals are visible only to you and any co-keepers you explicitly invite. We never sell or share your data, and you can export or delete everything at any time.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Absolutely — the Pro and Team plans are month-to-month (or annual, you choose). Cancel from Settings → Billing in one click. No phone calls, no retention scripts. You keep your data forever in the Free tier.",
  },
  {
    q: "Does it work on iPhone and Android?",
    a: "Yes. The app is a responsive web app that works in any modern browser — Safari, Chrome, Firefox, Edge. Voice journaling uses your phone's microphone. We're also working on native iOS/Android wrappers for 2026.",
  },
  {
    q: "What if I only have one hive?",
    a: "The Free tier is built for you. 1 yard, unlimited inspection journals, 5 AI diagnoses per month, 10 video uploads. Most first-year beekeepers stay on Free for the entire season and upgrade when they add a second hive or yard.",
  },
  {
    q: "What if I have 50+ hives?",
    a: "Pro handles unlimited hives across unlimited yards. For commercial sideliners or bee clubs sharing access across multiple keepers, Team adds unlimited co-keeper invites and priority support.",
  },
  {
    q: "How accurate is the AI diagnose?",
    a: "AI diagnose flags likely issues from photos (varroa, chalkbrood, AFB, queen problems, etc.) — it's a triage tool, not a replacement for hands-on inspection. We're transparent about confidence and always recommend confirming with a follow-up inspection.",
  },
  {
    q: "Can I export my data?",
    a: "Pro plans include CSV exports for journals, harvests, hives and treatments — plus a printable annual report. Your records belong to you, period.",
  },
  {
    q: "What's the 14-day trial?",
    a: "When you create an account, you start on a free 14-day Pro trial — full access to every feature, no credit card required. At day 14 you drop to Free unless you choose to subscribe.",
  },
  {
    q: "Who built this?",
    a: "Beekeepers, frustrated with notebooks getting wet, photos lost in camera rolls, and spreadsheets nobody updates. Every feature here is something we wished we had during our own inspections.",
  },
];

export default function FAQ() {
  return (
    <section
      id="spy-faq"
      className="bg-stone-50"
      data-testid="landing-faq"
    >
      <div className="max-w-3xl mx-auto px-6 py-24 sm:py-32">
        <header className="text-center mb-12">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-700 mb-3">Frequently asked</div>
          <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight text-stone-900">
            The questions every beekeeper asks before signing up.
          </h2>
        </header>
        <div className="divide-y divide-stone-200 border-y border-stone-200" itemScope itemType="https://schema.org/FAQPage">
          {FAQS.map((f, i) => <FaqItem key={i} {...f} index={i} />)}
        </div>
      </div>
    </section>
  );
}

function FaqItem({ q, a, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div
      itemScope
      itemProp="mainEntity"
      itemType="https://schema.org/Question"
      data-testid={`faq-item-${index}`}
      className="py-5"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left gap-4 group"
        aria-expanded={open}
        data-testid={`faq-toggle-${index}`}
      >
        <span itemProp="name" className="font-display text-lg font-medium text-stone-900 group-hover:text-amber-700 transition-colors">
          {q}
        </span>
        <ChevronDown
          className={`w-5 h-5 shrink-0 text-stone-500 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div
          itemScope
          itemProp="acceptedAnswer"
          itemType="https://schema.org/Answer"
          className="mt-3 text-sm text-stone-600 leading-relaxed"
          data-testid={`faq-answer-${index}`}
        >
          <p itemProp="text">{a}</p>
        </div>
      )}
    </div>
  );
}
